<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { completeOrder, getOrder, listOrders, shipOrder, listStickerFiles, stickerFileUrl, type AdminOrder, type OrderDetail } from "../api/orders";

type OrderStatusFilter = "ALL" | "PENDING_PAYMENT" | "PAID" | "SHIPPED" | "COMPLETED" | "CANCELED";

const orderStatusOptions: Array<{ value: OrderStatusFilter; label: string }> = [
  { value: "ALL", label: "全部订单" },
  { value: "PENDING_PAYMENT", label: "待付款" },
  { value: "PAID", label: "已付款" },
  { value: "SHIPPED", label: "已发货" },
  { value: "COMPLETED", label: "已完成" },
  { value: "CANCELED", label: "已取消" },
];

const orderStatusLabels: Record<string, string> = {
  PENDING_PAYMENT: "待付款",
  PAID: "已付款",
  SHIPPED: "已发货",
  COMPLETED: "已完成",
  CANCELED: "已取消",
};

const paymentStatusLabels: Record<string, string> = {
  PENDING: "支付处理中",
  SUCCESS: "支付成功",
  FAILED: "支付失败",
  CLOSED: "支付已关闭",
  UNPAID: "未支付",
};

const orders = ref<AdminOrder[]>([]);
const selected = ref<OrderDetail | null>(null);
const files=ref<Array<{id:string;originalFilename:string}>>([]);
async function openStickerFile(id:string){if(!selected.value)return;try{const result=await stickerFileUrl(selected.value.orderNo,id);window.open(result.url,"_blank","noopener,noreferrer");}catch(errorValue){actionError.value=messageFrom(errorValue,"文件不可用");}}
const selectedOrderNo = ref<string | null>(null);
const loading = ref(true);
const detailLoading = ref(false);
const actionBusy = ref(false);
const error = ref("");
const detailError = ref("");
const actionError = ref("");
const searchQuery = ref("");
const statusFilter = ref<OrderStatusFilter>("ALL");

const money = (value: number): string => `¥${(value / 100).toFixed(2)}`;

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function messageFrom(errorValue: unknown, fallback: string): string {
  return errorValue instanceof Error ? errorValue.message : fallback;
}

function orderStatusText(status: string): string {
  return orderStatusLabels[status] ?? status;
}

function paymentStatusText(status: string): string {
  return paymentStatusLabels[status] ?? status;
}

function displayOrderNo(order: { displayOrderNo: string | null; orderNo: string }): string {
  return order.displayOrderNo ?? order.orderNo;
}

function statusClass(prefix: string, status: string): string {
  const normalized = status.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `${prefix}--${normalized}`;
}

function specText(value: unknown): string {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return Object.values(value as Record<string, unknown>)
    .filter((item): item is string | number => typeof item === "string" || typeof item === "number")
    .join(" / ");
}

const filteredOrders = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();

  return orders.value.filter((order) => {
    const searchableText = [
      order.orderNo,
      order.displayOrderNo,
      order.userId,
      order.status,
      orderStatusText(order.status),
      order.paymentStatus,
      paymentStatusText(order.paymentStatus),
      order.createdAt,
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    const matchesSearch = !query || searchableText.includes(query);
    const matchesStatus = statusFilter.value === "ALL" || order.status === statusFilter.value;
    return matchesSearch && matchesStatus;
  });
});

async function load(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    orders.value = await listOrders();
  } catch (errorValue) {
    error.value = messageFrom(errorValue, "订单加载失败");
  } finally {
    loading.value = false;
  }
}

async function open(orderNo: string): Promise<void> {
  selectedOrderNo.value = orderNo;
  selected.value = null;
  detailLoading.value = true;
  detailError.value = "";
  actionError.value = "";
  try {
    selected.value = await getOrder(orderNo);
    files.value=selected.value.orderKind==="CUSTOM"?await listStickerFiles(orderNo):[];
  } catch (errorValue) {
    detailError.value = messageFrom(errorValue, "订单详情加载失败");
  } finally {
    detailLoading.value = false;
  }
}

function closeDetail(): void {
  selected.value = null;
  selectedOrderNo.value = null;
  detailError.value = "";
  actionError.value = "";
}

async function ship(): Promise<void> {
  if (!selected.value || selected.value.status !== "PAID" || actionBusy.value) return;
  actionBusy.value = true;
  actionError.value = "";
  try {
    selected.value = await shipOrder(selected.value.orderNo);
    await load();
  } catch (errorValue) {
    actionError.value = messageFrom(errorValue, "订单发货失败，请稍后重试");
  } finally {
    actionBusy.value = false;
  }
}

async function complete(): Promise<void> {
  if (!selected.value || selected.value.status !== "SHIPPED" || actionBusy.value) return;
  if (!window.confirm("确认将该订单标记为已完成？完成后可能触发推荐奖励等后续业务。")) return;
  actionBusy.value = true;
  actionError.value = "";
  try {
    selected.value = await completeOrder(selected.value.orderNo);
    await load();
  } catch (errorValue) {
    actionError.value = messageFrom(errorValue, "订单完成失败，请稍后重试");
  } finally {
    actionBusy.value = false;
  }
}

onMounted(load);
</script>

<template>
  <section class="orders-page">
    <header class="orders-header">
      <div>
        <h1>订单管理</h1>
        <p class="orders-intro">
          查看订单快照并对已付款订单执行整单发货；当前不接物流接口。
        </p>
      </div>
      <span class="orders-loaded">当前已加载 {{ orders.length }} 条订单</span>
    </header>

    <section class="orders-workspace">
      <div class="orders-toolbar">
        <div>
          <h2>订单列表</h2>
          <p>仅筛选当前已加载的真实订单，接口最多返回 100 条。</p>
        </div>
        <label class="orders-search">
          <span>搜索</span>
          <input
            v-model="searchQuery"
            aria-label="搜索当前已加载订单"
            placeholder="订单号、用户 ID 或状态"
            type="search"
          >
        </label>
      </div>

      <nav
        aria-label="订单状态筛选"
        class="orders-status-tabs"
      >
        <button
          v-for="option in orderStatusOptions"
          :key="option.value"
          :class="{ 'orders-status-tab--active': statusFilter === option.value }"
          type="button"
          @click="statusFilter = option.value"
        >
          {{ option.label }}
        </button>
      </nav>

      <div
        v-if="loading"
        class="orders-feedback orders-feedback--loading"
      >
        正在加载订单…
      </div>
      <div
        v-else-if="error"
        aria-live="polite"
        class="orders-feedback orders-feedback--error"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="orders-table-card"
      >
        <div class="orders-table-wrap">
          <table class="orders-table">
            <thead>
              <tr>
                <th scope="col">
                  订单号 / 创建时间
                </th>
                <th scope="col">
                  用户
                </th>
                <th scope="col">
                  订单金额
                </th>
                <th scope="col">
                  支付状态
                </th>
                <th scope="col">
                  订单状态
                </th>
                <th scope="col">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in filteredOrders"
                :key="order.id"
                :class="{ 'orders-row--selected': selectedOrderNo === order.orderNo }"
              >
                <td class="orders-order-cell">
                  <strong>{{ displayOrderNo(order) }}</strong>
                  <time :datetime="order.createdAt">{{ formatDate(order.createdAt) }}</time>
                </td>
                <td>
                  <span class="orders-user-id">{{ order.userId }}</span>
                </td>
                <td class="orders-amount">
                  {{ money(order.payableAmount) }}
                </td>
                <td>
                  <span
                    class="status-badge status-badge--payment"
                    :class="statusClass('status-badge--payment', order.paymentStatus)"
                  >
                    {{ paymentStatusText(order.paymentStatus) }}
                  </span>
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="statusClass('status-badge', order.status)"
                  >
                    {{ orderStatusText(order.status) }}
                  </span>
                </td>
                <td class="orders-actions">
                  <button
                    :disabled="detailLoading"
                    type="button"
                    @click="open(order.orderNo)"
                  >
                    {{ detailLoading && selectedOrderNo === order.orderNo ? "加载中…" : "查看详情" }}
                  </button>
                </td>
              </tr>
              <tr v-if="!filteredOrders.length">
                <td
                  class="orders-empty"
                  colspan="6"
                >
                  <strong>{{ orders.length ? "没有匹配的订单" : "当前没有已加载订单" }}</strong>
                  <p>{{ orders.length ? "请调整搜索词或状态筛选。" : "当前接口没有返回订单记录。" }}</p>
                  <button
                    v-if="searchQuery || statusFilter !== 'ALL'"
                    type="button"
                    @click="searchQuery = ''; statusFilter = 'ALL'"
                  >
                    清除筛选
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="orders-table-footer">
          <span>显示 {{ filteredOrders.length }} / {{ orders.length }} 条订单</span>
          <span>仅展示接口当前已返回的真实订单</span>
        </footer>
      </div>
    </section>

    <article
      v-if="selected || detailLoading || detailError"
      class="orders-detail-card"
    >
      <header class="orders-detail-header">
        <div>
          <h2>订单详情</h2>
          <p>{{ selected ? displayOrderNo(selected) : selectedOrderNo ?? "订单" }}</p>
        </div>
        <button
          class="quiet-button"
          type="button"
          @click="closeDetail"
        >
          收起详情
        </button>
      </header>

      <div
        v-if="detailLoading"
        class="orders-feedback orders-feedback--loading"
      >
        正在加载订单详情…
      </div>
      <div
        v-else-if="detailError"
        aria-live="polite"
        class="orders-feedback orders-feedback--error"
      >
        {{ detailError }}
        <button
          class="quiet-button"
          type="button"
          @click="selectedOrderNo && open(selectedOrderNo)"
        >
          重新加载
        </button>
      </div>
      <div
        v-else-if="selected"
        class="orders-detail-content"
      >
        <div class="orders-detail-overview">
          <div>
            <span>订单状态</span>
            <strong
              class="status-badge"
              :class="statusClass('status-badge', selected.status)"
            >
              {{ orderStatusText(selected.status) }}
            </strong>
          </div>
          <div>
            <span>支付状态</span>
            <strong
              class="status-badge status-badge--payment"
              :class="statusClass('status-badge--payment', selected.paymentStatus)"
            >
              {{ paymentStatusText(selected.paymentStatus) }}
            </strong>
          </div>
          <div class="orders-detail-amounts">
            <span>商品金额</span>
            <strong>{{ money(selected.productAmount) }}</strong>
          </div>
          <div class="orders-detail-amounts">
            <span>运费</span>
            <strong>{{ money(selected.freightAmount) }}</strong>
          </div>
          <div class="orders-detail-amounts orders-detail-amounts--total">
            <span>应付金额</span>
            <strong>{{ money(selected.payableAmount) }}</strong>
          </div>
        </div>

        <div class="orders-detail-grid">
          <section class="orders-detail-section">
            <h3>收货信息</h3>
            <dl class="receiver-details">
              <div>
                <dt>收货人</dt>
                <dd>{{ selected.receiver.name }}</dd>
              </div>
              <div>
                <dt>联系电话</dt>
                <dd>{{ selected.receiver.mobile }}</dd>
              </div>
              <div>
                <dt>收货地址</dt>
                <dd>
                  {{ selected.receiver.province }}{{ selected.receiver.city }}{{ selected.receiver.district }}{{ selected.receiver.detail }}
                </dd>
              </div>
            </dl>
          </section>

          <section class="orders-detail-section orders-detail-section--items">
            <h3>订单明细</h3>
            <div v-if="selected.orderKind === 'CUSTOM'">
              <button
                v-for="file in files"
                :key="file.id"
                @click="openStickerFile(file.id)"
              >
                {{ file.originalFilename }}
              </button>
            </div>
            <div class="order-items">
              <div
                v-for="item in selected.items"
                :key="item.id"
                class="order-item"
              >
                <div class="order-item__main">
                  <strong>{{ item.productName }}</strong>
                  <span>规格：{{ specText(item.skuSpec) || "—" }}</span>
                  <small v-if="selected.orderKind !== 'CUSTOM'">SKU：{{ item.skuNo }}</small>
                </div>
                <span v-if="selected.orderKind === 'CUSTOM'">每款{{ item.quantityPerDesign }}张 · {{ item.designCount }}款 · 含包装{{ item.packedWeightGrams }}克</span><span v-else>{{ item.tierPieceQty }} 个档 × {{ item.purchaseCount }} 份</span>
                <strong>{{ money(item.lineAmount) }}</strong>
              </div>
            </div>
          </section>
        </div>

        <div
          v-if="actionError"
          aria-live="polite"
          class="orders-feedback orders-feedback--error"
        >
          {{ actionError }}
        </div>
        <div
          v-if="selected.status === 'PAID'"
          class="orders-detail-actions"
        >
          <p>已付款订单可执行整单发货，当前不接物流接口。</p>
          <button
            :disabled="actionBusy"
            type="button"
            @click="ship"
          >
            {{ actionBusy ? "发货中…" : "整单发货" }}
          </button>
        </div>
        <div
          v-else-if="selected.status === 'SHIPPED'"
          class="orders-detail-actions"
        >
          <p>已发货订单可标记为完成，推荐奖励将在首个达标订单完成时发放。</p>
          <button
            :disabled="actionBusy"
            type="button"
            @click="complete"
          >
            {{ actionBusy ? "完成中…" : "完成订单" }}
          </button>
        </div>
      </div>
    </article>
  </section>
</template>

<style scoped>
.orders-page {
  display: grid;
  gap: 24px;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 0 136px;
  color: var(--admin-ink);
}

.orders-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
}

.orders-header h1 {
  margin: 0 0 8px;
  font-size: clamp(32px, 3vw, 42px);
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.orders-intro {
  max-width: 54ch;
  margin: 0;
  color: var(--admin-muted);
  font-size: 15px;
  line-height: 1.65;
}

.orders-loaded {
  padding-bottom: 4px;
  color: var(--admin-muted);
  font-size: 13px;
  white-space: nowrap;
}

.orders-workspace,
.orders-detail-card {
  overflow: hidden;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 16px;
  background: var(--admin-surface);
  box-shadow: 0 4px 20px rgba(28, 67, 46, 0.04);
}

.orders-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.orders-toolbar h2,
.orders-detail-header h2 {
  margin: 0;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.orders-toolbar p,
.orders-detail-header p {
  margin: 6px 0 0;
  color: var(--admin-muted);
  font-size: 13px;
  line-height: 1.5;
}

.orders-search {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: min(330px, 100%);
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.orders-search input {
  width: 260px;
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid var(--admin-outline);
  border-radius: 9px;
  background: var(--admin-surface-low);
  color: var(--admin-ink);
}

.orders-status-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 16px 28px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.orders-status-tabs button,
.orders-actions button,
.orders-empty button,
.orders-detail-actions button,
.quiet-button {
  min-height: 40px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--admin-muted);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.orders-status-tabs button:hover,
.orders-status-tab--active {
  border-color: rgba(60, 179, 113, 0.24) !important;
  background: var(--admin-primary-soft) !important;
  color: var(--admin-primary-deep) !important;
}

.orders-table-card {
  min-width: 0;
}

.orders-table-wrap {
  overflow-x: auto;
}

.orders-table {
  width: 100%;
  min-width: 780px;
  border-collapse: collapse;
  text-align: left;
}

.orders-table th,
.orders-table td {
  padding: 16px 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
  vertical-align: top;
}

.orders-table th {
  background: var(--admin-surface-low);
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.orders-table td {
  color: var(--admin-ink);
  font-size: 14px;
}

.orders-table tbody tr {
  transition: background-color 160ms ease;
}

.orders-table tbody tr:hover,
.orders-row--selected {
  background: #fbfdfb;
}

.orders-order-cell {
  min-width: 220px;
}

.orders-order-cell strong,
.orders-order-cell time,
.orders-user-id {
  display: block;
}

.orders-order-cell strong {
  color: var(--admin-ink);
  font-size: 14px;
  line-height: 1.45;
}

.orders-order-cell time {
  margin-top: 5px;
  color: var(--admin-muted);
  font-size: 12px;
}

.orders-user-id {
  color: var(--admin-muted);
  font-variant-numeric: tabular-nums;
}

.orders-amount {
  color: var(--admin-ink);
  font-weight: 800;
  white-space: nowrap;
}

.orders-actions {
  text-align: right;
  white-space: nowrap;
}

.orders-actions button,
.orders-detail-actions button {
  background: var(--admin-primary);
  color: #fff;
}

.orders-actions button:hover,
.orders-detail-actions button:hover {
  background: var(--admin-primary-deep);
}

.orders-actions button:disabled,
.orders-detail-actions button:disabled,
.orders-status-tabs button:disabled,
.orders-empty button:disabled,
.quiet-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.status-badge {
  display: inline-flex;
  min-width: 68px;
  justify-content: center;
  padding: 5px 9px;
  border: 1px solid #d7ded9;
  border-radius: 7px;
  background: #f5f7f6;
  color: #53605a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
}

.status-badge--pending-payment,
.status-badge--payment--pending {
  border-color: #f0d9ad;
  background: #fff8e8;
  color: #99630d;
}

.status-badge--paid,
.status-badge--payment--success {
  border-color: #b8dfc8;
  background: #f0faf4;
  color: #248a55;
}

.status-badge--shipped {
  border-color: #c7d9ec;
  background: #f1f7ff;
  color: #356b9f;
}

.status-badge--completed {
  border-color: #d7ded9;
  background: #f5f7f6;
  color: #53605a;
}

.status-badge--canceled,
.status-badge--payment--failed,
.status-badge--payment--closed {
  border-color: #e8caca;
  background: #fff5f5;
  color: #a34848;
}

.status-badge--payment--unpaid {
  border-color: #d7ded9;
  background: #f5f7f6;
  color: #53605a;
}

.orders-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  color: var(--admin-muted);
  font-size: 12px;
}

.orders-feedback {
  margin: 20px 28px;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.orders-feedback--loading {
  border: 1px dashed var(--admin-outline);
  color: var(--admin-muted);
  text-align: center;
}

.orders-feedback--error {
  border: 1px solid #e8caca;
  background: #fff5f5;
  color: #a34848;
}

.orders-empty {
  padding: 48px 20px !important;
  color: var(--admin-muted) !important;
  text-align: center;
}

.orders-empty strong {
  display: block;
  color: var(--admin-ink);
  font-size: 15px;
}

.orders-empty p {
  margin: 8px 0 16px;
}

.orders-empty button {
  border-color: rgba(60, 179, 113, 0.24);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.orders-detail-card {
  padding: 28px;
}

.orders-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--admin-outline-soft);
}

.orders-detail-header h2 {
  font-size: 20px;
}

.orders-detail-header p {
  font-variant-numeric: tabular-nums;
}

.quiet-button {
  border-color: var(--admin-outline-soft);
  background: var(--admin-surface-low);
}

.quiet-button:hover {
  border-color: rgba(60, 179, 113, 0.32);
  color: var(--admin-primary-deep);
}

.orders-detail-content {
  display: grid;
  gap: 24px;
  padding-top: 24px;
}

.orders-detail-overview {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.orders-detail-overview > div {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 14px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 10px;
  background: var(--admin-surface-low);
}

.orders-detail-overview > div > span,
.orders-detail-amounts strong {
  color: var(--admin-muted);
  font-size: 12px;
}

.orders-detail-amounts strong {
  color: var(--admin-ink);
  font-size: 17px;
}

.orders-detail-amounts--total {
  border-color: rgba(60, 179, 113, 0.32) !important;
  background: var(--admin-primary-soft) !important;
}

.orders-detail-amounts--total strong {
  color: var(--admin-primary-deep);
}

.orders-detail-grid {
  display: grid;
  grid-template-columns: minmax(260px, 0.8fr) minmax(0, 1.2fr);
  gap: 20px;
}

.orders-detail-section {
  padding: 20px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 12px;
}

.orders-detail-section h3 {
  margin: 0 0 16px;
  font-size: 15px;
}

.receiver-details {
  display: grid;
  gap: 14px;
  margin: 0;
}

.receiver-details > div {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  gap: 12px;
}

.receiver-details dt {
  color: var(--admin-muted);
  font-size: 12px;
}

.receiver-details dd {
  margin: 0;
  color: var(--admin-ink);
  font-size: 13px;
  line-height: 1.55;
}

.order-items {
  display: grid;
  gap: 12px;
}

.order-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--admin-outline-soft);
  color: var(--admin-muted);
  font-size: 13px;
}

.order-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.order-item__main {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.order-item__main strong {
  overflow: hidden;
  color: var(--admin-ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-item__main span,
.order-item__main small {
  overflow: hidden;
  color: var(--admin-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-item > strong {
  color: var(--admin-ink);
  white-space: nowrap;
}

.orders-detail-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 4px;
}

.orders-detail-actions p {
  margin: 0;
  color: var(--admin-muted);
  font-size: 13px;
}

button:focus-visible,
input:focus-visible {
  outline: 3px solid rgba(60, 179, 113, 0.25);
  outline-offset: 2px;
}

@media (max-width: 900px) {
  .orders-page {
    width: min(100% - 32px, 720px);
    padding-top: 22px;
  }

  .orders-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .orders-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .orders-search {
    width: 100%;
  }

  .orders-search input {
    flex: 1;
    width: auto;
  }

  .orders-detail-overview {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .orders-detail-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .orders-page {
    width: min(100% - 24px, 560px);
    padding-bottom: 154px;
  }

  .orders-toolbar,
  .orders-status-tabs {
    padding-right: 18px;
    padding-left: 18px;
  }

  .orders-table-footer,
  .orders-detail-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .orders-detail-card {
    padding: 20px;
  }

  .orders-detail-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .orders-detail-header {
    align-items: stretch;
    flex-direction: column;
  }

  .orders-detail-header .quiet-button {
    align-self: flex-start;
  }

  .order-item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .order-item > strong {
    grid-column: 2;
    grid-row: 1;
  }

  .order-item > span {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}
</style>
